import React, { useState, useRef, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import {
  Typography,
  Paper,
  Box,
  CircularProgress,
} from '@mui/material';

import { serverUrl, configKeys, useConfig } from './ConfigHolder';
import { SpeechSynthesizer, synthesizeAudio } from './SpeechSynthesizer';

import { saveAs } from 'file-saver';
import { post_auth_mp, post_auth } from './auth';
import { endSpeech, sample_interval } from './speechAnalysis';
import { DisplayTranscriptionResults, FormResults } from './FormResults';
import YouSaid from './components/YouSaid';
import _ from 'lodash';
import { translateText } from './FormTranslate';
import { Sentence } from './models/Sentence';
import ControlPanel from './ControlPanel';
import { fetchAudioUrl } from './audioUtils';
import ErrorDispatcher from './ErrorDispatcher';

import { useAutoPlay } from './contexts/AutoPlayContext';

// VoiceRecorder is responsible for:
//   - Capturing microphone audio and sending it to the backend for transcription
//     and quality evaluation.
//   - Maintaining **session-scoped** practice data in memory:
//       * practiceText        – the text the learner is trying to say.
//       * sessionAttempts     – array of all attempts for the current practiceText
//                               in this UI session.
//       * sessionBestScore    – best score within sessionAttempts.
//       * sessionQualityChartUrl – URL of a backend-generated figure summarizing
//                               session quality.
//   - Passing the practiceText into SpeechSynthesizer so that editing the text
//     resets the session attempts while keeping the persisted MongoDB fields
//     (phraseVal.history, phraseVal.bestScore) unchanged.

// eslint-disable-next-line
const saveAudioLevels = (audioLevelArray) => {
  const arrayAsString = JSON.stringify(audioLevelArray);
  const blob = new Blob([arrayAsString], { type: 'text/plain;charset=utf-8' });
  saveAs(blob, 'audioLevels.json');
};

function VoiceRecorder({
  sentence,
  sentenceSource,
  isShiftedLeft,
  sentenceCallBack,
  toggleMode,
  dualView,
  isCurrentMyContentEmpty = false,
  errorCallBack = null,
}) {
  const [recording, setRecording] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const [waitTranscription, setWaitTranscription] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  // practiceText: target text the learner is practicing against (mirrored in
  // SpeechSynthesizer textarea). Changing this resets the current session
  // attempts and best score, but does not touch global MongoDB history.
  const [practiceText, setPracticeText] = useState('');
  const [targetDurationSec, setTargetDurationSec] = useState(0);
  const [hideModalResults, setHideModalResults] = useState(false);
  const mediaRecorder = useRef(null);

  const [playbackRate, setPlaybackRate] = useState(1.0);
  let silenceTimer = undefined;

  let dataCollectionIntervalMS = sample_interval;
  const dataCollectionInterval = useRef();
  const audioLevelArray = useRef([]);

  const [lastAttemptResult, setLastAttemptResult] = useState({});
  const [sessionAttempts, setSessionAttempts] = useState([]);
  const [sessionBestScore, setSessionBestScore] = useState({});

  const [sessionQualityChartUrl, setSessionQualityChartUrl] = useState(null);
  const [playBackInProgress, setPlayBackInProgress] = useState(false);
  const [hideAllResults, setHideAllResults] = useState(false);
  const [activeSentence, setActiveSentence] = useState(() =>
    sentence ? sentence.deepCopy() : null
  );
  const [activeSentenceSource, setActiveSentenceSource] = useState(sentenceSource || null);
  const { config, updateConfig } = useConfig();
  const streamRef = useRef(null);
  const sourceRef = useRef(null);
  const [transcriptionError, setTranscriptionError] = useState(null);
  const [error, setError] = useState(null);
  const [triggerPlay, setTriggerPlay] = useState(0)

  const ctx = useAutoPlay();
  
  // ADD THIS REF TO HOLD DEFERRED UPDATES
  const pendingSentenceUpdateRef = useRef(null);

  // SAFEGUARD: Flush it upward if the user forcefully navigates away
  useEffect(() => {
    return () => {
      if (pendingSentenceUpdateRef.current) {
        sentenceCallBack(pendingSentenceUpdateRef.current);
      }
    };
  }, []);

  // Request microphone access on component load
  useEffect(() => {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        stream.getTracks().forEach((track) => track.stop());
        stream = null;
        console.log('Microphone access granted');
      })
      .catch((error) => {
        console.error('Error accessing microphone:', error);
      });

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const handlePlaybackRateChange = (event) => {
    setPlaybackRate(parseFloat(event.target.value));
  };

  const resetPlaybackRate = () => {
    setPlaybackRate(1);
  };

  useEffect(() => {
    setHideAllResults(true);
    setPracticeText('');
    setActiveSentence(null);
    setSessionAttempts([]);
    setSessionBestScore({});
    setAudioURL(null);
    setRecorded(false);
    setSessionQualityChartUrl(null);
    // eslint-disable-next-line
  }, [config[configKeys.langLearn], config[configKeys.currentContent]]);

  // useEffect(() => {
  //   if (!_.isEmpty(sentence)) {
  //     if (
  //       _.isEmpty(activeSentence) ||
  //       !_.isEqual(sentence, activeSentence) ||
  //       // sentence.isConvo ||
  //       ctx.autoPlayState !== 'idle'
  //     ) {
  //       handlePracticeTextChange(sentence.getSentence());
  //       setActiveSentence(sentence);
  //       setActiveSentenceSource(sentenceSource || null);
  //     }
  //     if (sentence.isConvo) {
  //       setTriggerPlay((prev) => prev + 1);
  //     }
  //   }
  //   // eslint-disable-next-line
  //   // console.log("Active Sentence: ", activeSentence, activeSentenceSource)
  //   // console.log('Sentence from VoiceRecorder', sentence)
  // }, [sentence]);

    useEffect(() => {
    if (!_.isEmpty(sentence)) {
      // Check if the actual text changed (meaning the user clicked a NEW sentence)
      const isNewSentenceText = _.isEmpty(activeSentence) || sentence.getSentence() !== activeSentence.getSentence();

      if (
        isNewSentenceText ||
        ctx.autoPlayState !== 'idle'
      ) {
        handlePracticeTextChange(sentence.getSentence());
        setActiveSentence(sentence);
        setActiveSentenceSource(sentenceSource || null);

        // ONLY trigger playback if this is a genuinely newly selected conversation sentence
        if (sentence.isConvo) {
          setTriggerPlay((prev) => prev + 1);
        }
      } else if (!_.isEqual(sentence, activeSentence)) {
        // The text is the same, but the object changed (e.g. backend scores updated after transcription).
        // Update the active sentence silently WITHOUT triggering an audio replay or wiping the UI.
        setActiveSentence(sentence);
      }
    }
  }, [sentence]);

  const startRecording = async () => {
    setRecorded(false);
    setTranscriptionError(false);
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.stop();
    }

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(async (stream) => {
        mediaRecorder.current = new MediaRecorder(stream, {});

        const audioChunks = [];
        let analyser = null;
        streamRef.current = stream;
        const audioContext = new AudioContext();
        if (audioContext) {
          console.log('initializing analyzer');
          analyser = audioContext.createAnalyser();
          analyser.fftSize = 1024;
          analyser.smoothingTimeConstant = 0.8;
          const source = audioContext.createMediaStreamSource(stream);
          sourceRef.current = source;
          source.connect(analyser);
        } else {
          console.log('Failed to initialize Analyser');
        }

        function calculateAudioLevel() {
          const dataArray = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(dataArray);
          let sum = dataArray
            .slice(1, 64)
            .reduce((acc, value) => acc + (value / 255) ** 2, 0);
          const averageAmplitude = sum / dataArray.length;
          const normalizedAmplitude = averageAmplitude;
          return normalizedAmplitude;
        }

        mediaRecorder.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunks.push(event.data);
            clearTimeout(silenceTimer);
          }
        };

        const cleanupAudioResources = () => {
          if (analyser) {
            analyser.disconnect();
            analyser = null;
          }
          if (sourceRef.current) {
            sourceRef.current.disconnect();
            sourceRef.current = null;
          }
          if (audioContext) {
            audioContext
              .close()
              .then(() => console.log('Audio context closed'))
              .catch((error) =>
                console.error('Error closing audio context:', error)
              );
          }
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
          }
        };

        mediaRecorder.current.onstop = async () => {
          console.log('mime ', mediaRecorder.mimeType);
          const audioBlob = new Blob(audioChunks, { type: 'audio/flac' });
          setPlaybackRate(1.0);
          setWaitTranscription(true);
          try {
            const saveResult = await saveAudioToCloud(audioBlob);
            if (saveResult) {
              const audioUrl = saveResult.fpath;
              setAudioURL(audioUrl);
              console.log('Saved to cloud', saveResult);
              transcribeAudio(saveResult);
              await playbackRecording(audioUrl);
            } else {
              setAudioURL(null);
              setWaitTranscription(false);
            }
          } finally {
            cleanupAudioResources();
          }
        };

        await mediaRecorder.current.start();
        setRecording(true);
        dataCollectionInterval.current = setInterval(() => {
          const audioLevel = calculateAudioLevel();
          audioLevelArray.current.push(audioLevel);
          const speechEnded = endSpeech(audioLevel);
          if (speechEnded) {
            stopRecording();
          }
        }, dataCollectionIntervalMS);
      })
      .catch((error) => {
        console.error('Error accessing microphone:', error);
      });
  };


  /**
   * Upload a recorded audio blob to the backend and return storage metadata.
   *
   * Frontend contract:
   *   - Sends a multipart/form-data POST with field `audioBlob` to
   *     `${serverUrl}/save-audio` using the authenticated `post_auth_mp` helper.
   *
   * Backend contract (see `save_audio_mp3` in `speech_services.py`):
   *   - Normalizes the audio (mono MP3), stores it under the user-specific
   *     folder, and returns an object of the form:
   *       {
   *         gcs_uri: string,      // Cloud Storage URI for the saved MP3
   *         rate: number,         // sample rate (Hz) used for STT
   *         format: string,       // e.g. 'mp3'
   *         fpath: string,        // path inside the user folder (used for playback)
   *         user_sfolder: string  // user root subfolder for later operations
   *       }
   *
      mcPhraseObj.phraseVal.attemptCount = sessionAttempts.length;
      mcPhraseObj.phraseVal.bestScore = sessionBestScore;
   */
  const saveAudioToCloud = async (audioBlob) => {
    let uri = serverUrl + '/save-audio';
    console.log('Saving to cloud:', audioBlob.size);
    const formData = new FormData();
    formData.append('audioBlob', audioBlob);
    try {
      const response = await post_auth_mp(uri, formData);
      return response.data;
    } catch (error) {
      console.error(error.response);
      if (errorCallBack) {
        errorCallBack(error);
      } else {
        setError(error);
      }

      return null;
    }
  };

  const transcribeAudio = async (cloudData) => {
    let uri = serverUrl + '/transcribe-cloud';
    try {
      const formData = new FormData();
      let lang_code = config[configKeys.langLearn].value;
      formData.append('lang_code', lang_code);
      formData.append('input_text', practiceText);
      formData.append('aim_time', targetDurationSec);
      formData.append('history', JSON.stringify(sessionAttempts));

      formData.append('gcs_uri', cloudData.gcs_uri);
      formData.append('format', cloudData.format);
      formData.append('rate', cloudData.rate);
      formData.append('user_sfolder', cloudData.user_sfolder);
      formData.append('fpath', cloudData.fpath);

      const response = await post_auth(uri, formData);

      const newResult = response.data.sp2t_res;
      setLastAttemptResult(newResult);
      const bestInSession = response.data.best_score;
      setSessionBestScore(bestInSession);
      console.log('best score', bestInSession);
      const newSessionAttempts = [...sessionAttempts, newResult];
      if (activeSentence && activeSentence.getSentence()) {
        // Update the in-memory sentence with the latest session-best result
        // and persist to Mongo. saveSentence() now returns the updated
        // phraseObj (including refreshed global history/bestScore) coming
        // back from the backend update_phrase_object endpoint.
        activeSentence.updateSentence(bestInSession, newResult);
        const saveResult = await activeSentence.saveSentence();

        // Prefer the bestScore object returned from the backend if present;
        // otherwise, fall back to our local bestSCore.
        const updatedBestScore = saveResult?.bestScore || activeSentence.bestScore;
        console.log('saveResult from saveSentence', saveResult);
        console.log('activeSentence.phraseVal before replacement', activeSentence.phraseVal);
        // Rebuild a fresh Sentence wrapper around the updated phraseObj so
        // that any consumers (e.g. SentenceStats) see the new global metrics
        // immediately without a full content reload.
        const updatedSentence = activeSentence.deepCopy();
        updatedSentence.bestScore = updatedBestScore;
        updatedSentence.play = false;

        setActiveSentence(updatedSentence); // keep equality with MainContent's sentence
        
        // --- NEW LOGIC: Delay the callback on Mobile so the Row doesn't unmount and kill our View ---
        if (isMobile || !dualView) {
          pendingSentenceUpdateRef.current = updatedSentence;
        } else {
          sentenceCallBack(updatedSentence);
        }

      } else {
        console.log('No sentence to update');
      }
      setSessionAttempts(newSessionAttempts);

      if (!practiceText) {
        setPracticeText(newResult.transcription);
      }
      const imageURL = response.data.q_res?.quality_fig || null;
      setSessionQualityChartUrl(imageURL);
      setHideAllResults(false);
      openFormResults();
      setWaitTranscription(false);
    } catch (error) {
      console.error('Error transcribing audio:', error);
      setWaitTranscription(false);
      // Force Safari to display the exact exception causing the silent failure
      setTranscriptionError(error.message || error.toString());
      // alert(`Diagnostic Error: ${error.message || error.toString()}`);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.stop();
      setRecording(false);
      setRecorded(true);
    }
    clearInterval(dataCollectionInterval.current);
    audioLevelArray.current = [];
  };

  const aimtimeUpdateCallBack = (durationSec) => {
    setTargetDurationSec(durationSec);
  };

  const handlePracticeTextChange = (newText) => {
    setPracticeText(newText);
    setHideAllResults(true);
    setSessionAttempts([]);
    setSessionBestScore({});
    setActiveSentence(null);
    setAudioURL(null);
    setRecorded(false);
    setSessionQualityChartUrl(null);
    setLastAttemptResult({ transcription: '' });
  };

  const [isModalResultsOpen, setIsModalResultsOpen] = useState(false);
  const openFormResults = () => {
    setIsModalResultsOpen(true);
  };
  
  const closeFormResults = () => {
    setIsModalResultsOpen(false);
    
    // --- FLUSH THE DEFERRED UPDATE HERE ---
    if (pendingSentenceUpdateRef.current) {
      sentenceCallBack(pendingSentenceUpdateRef.current);
      pendingSentenceUpdateRef.current = null;
    }
  };

  const playbackRecording = async (aURL) => {
    setPlayBackInProgress(true);
    let retryAttempt = 0;
    const maxRetries = 1;

    const playAudio = async () => {
      if (aURL) {
        const signedUrl = await fetchAudioUrl(aURL);
        const au = new Audio(signedUrl);
        au.playbackRate = playbackRate;

        try {
          await new Promise((resolve, reject) => {
            au.addEventListener('ended', () => {
              resolve();
            });
            au.play().catch(reject);
          });
        } catch (error) {
          console.error('Playback failed:', error);
          if (retryAttempt < maxRetries) {
            retryAttempt += 1;
            console.log(`Retrying playback, attempt ${retryAttempt}`);
            await playAudio();
          } else {
            console.error(
              'Max retry attempts reached. Playback failed permanently.'
            );
          }
        } finally {
          au.src = '';
          au.load();
          au.remove();
        }
      }
    };

    await playAudio();
    setPlayBackInProgress(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const addToContentCallBackVR = async (myCobj) => {
    let text = myCobj.text;
    let langLearn = config[configKeys.langLearn];
    let langBase = config[configKeys.langBase];
    let content = config[configKeys.currentContent];
    try {
      if (content !== 'MyContent') {
        content = 'MyContent';
        await updateConfig(configKeys.currentContent, content);
      }

      let trans_text = await translateText(
        text,
        langBase.value,
        langLearn.value
      );

  const mcPhraseObj = {};
  mcPhraseObj.phrase = text;
  mcPhraseObj.phraseVal = {};
  mcPhraseObj.phraseVal.attemptCount = sessionAttempts.length;
  mcPhraseObj.phraseVal.bestScore = sessionBestScore;
  mcPhraseObj.phraseVal.history = sessionAttempts;
      mcPhraseObj.phraseVal.translation = trans_text;
      mcPhraseObj.phraseVal.explanations = myCobj.explanations;

      const genders = ['FEMALE', 'MALE'];
      const store_voice = { Base: langBase.label, content };
      const voices = {};
      for (const gender of genders) {
        const voiceObj = await synthesizeAudio(
          text,
          langLearn.value,
          gender,
          langLearn.label,
          serverUrl,
          store_voice
        );
        voices[gender] = voiceObj;
      }
      mcPhraseObj.phraseVal.voices = voices;
      console.log('mcPhraseObj', mcPhraseObj);

      const mcSentence = Sentence.createPhraseWrap(mcPhraseObj);
      mcSentence.addToMyContent = true;
      mcSentence.insertMode = myCobj.insertMode;
      sentenceCallBack(mcSentence);
    } catch (err) {
      console.error('Failed to create sentence', err);
      if (errorCallBack) {
        errorCallBack(error); 
      } else {
        setError(error);
      }

    }
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        margin: '0 auto',
        alignItems: 'flex-start',
      }}
    >
      {error ? (
        <ErrorDispatcher
          error={error}
          resetError={() => setError(null)}
          clickAwayExit={true}
          navigateToLogin={false}
          showDialog={false}
          onEnableAccountSuccess={() => {
            console.log('Voice Recorder: Account enabled');
            setError(null);
          }}
        />
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            sx={{ width: '50%', height: 6 }}
          />
          <Box sx={{ mt: 0, ml: 1, width: '97%' }}>
            <SpeechSynthesizer
              inputTextCallBack={handlePracticeTextChange}
              aimtimeUpdateCallBack={aimtimeUpdateCallBack}
              inputText={practiceText}
              inputSentence={activeSentence}
              source={activeSentenceSource}
              addToContentCallBackVR={addToContentCallBackVR}
              sentenceCallBack={sentenceCallBack}
              toggleMode={toggleMode}
              dualView={dualView}
              isCurrentMyContentEmpty={isCurrentMyContentEmpty}
              errorCallBack={(err) => errorCallBack(err)}
              triggerPlay={triggerPlay}
            />
          </Box>

      {!ctx.ui.voiceRecordingDisabled && (
        <Box sx={{ mt: 2, ml: 1, width: '97%' }}>
          <ControlPanel
            recording={recording}
            startRecording={startRecording}
            stopRecording={stopRecording}
            waitTranscription={waitTranscription}
            language={config[configKeys.langLearn].label}
            playbackRate={playbackRate}
            handlePlaybackRateChange={handlePlaybackRateChange}
            resetPlaybackRate={resetPlaybackRate}
            playBackInProgress={playBackInProgress}
            recorded={recorded}
            playbackRecording={playbackRecording}
            audioURL={audioURL}
          />
        </Box>
      )}

      {recorded && !waitTranscription && !hideAllResults ? (
        <Box sx={{ mt: 2, ml: 0, alignItems: 'flex-start', width: '100%' }}>
          <Paper
            elevation={3}
            sx={{
              backgroundColor: 'lavender',
              px: 0,
              pt: 1,
              pb: 3,
              borderRadius: 2,
              position: 'relative',
              width: '97%',
              margin: '0 auto',
              alignItems: 'flex-start',
            }}
          >
            <Box sx={{ mt: 0, textAlign: 'left' }}>
              <YouSaid 
                value={lastAttemptResult.transcription} 
                lang={config[configKeys.langLearn].label}
              />
              <DisplayTranscriptionResults
                imageURL={sessionQualityChartUrl}
                respArray={sessionAttempts}
                bestScore={sessionBestScore}
              />
            </Box>
          </Paper>
          {recorded && isMobile && (
            <FormResults
              isModalOpen={isModalResultsOpen && !hideModalResults}
              transcription={lastAttemptResult.transcription}
              onClose={closeFormResults}
              qualityImage={sessionQualityChartUrl}
              responseArray={sessionAttempts}
              bestScore={sessionBestScore}
              onHide={(hide) => {
                setHideModalResults(hide);
              }}
            />
          )}
        </Box>
      ) : waitTranscription ? (
        <Box sx={{ display: 'flex', m: '10%' }}>
          <CircularProgress />
        </Box>
      ) : null}

         </>
      )}

    </Box>
  );
}

export default VoiceRecorder;
